<template>
  <div class="eth-fc">
    <div class="eth-fc-header">
      <div class="eth-fc-header-left">
        <div class="eth-fc-btn-group">
          <button type="button" class="eth-fc-btn eth-fc-btn--group-first" @click="prevMonth" title="Previous month">
            <i class="pi pi-chevron-left"></i>
          </button>
          <button type="button" class="eth-fc-btn eth-fc-btn--group-last" @click="nextMonth" title="Next month">
            <i class="pi pi-chevron-right"></i>
          </button>
        </div>
        <button type="button" class="eth-fc-btn eth-fc-btn--today" @click="goToday">Today</button>
      </div>
      <h2 class="eth-fc-title">{{ monthTitle }}</h2>
      <div class="eth-fc-header-right"></div>
    </div>

    <table class="eth-fc-table">
      <thead>
        <tr>
          <th v-for="h in weekHeaders" :key="h" class="eth-fc-col-header">{{ h }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(week, wIdx) in weeks" :key="wIdx">
          <td
            v-for="(cell, dIdx) in week"
            :key="dIdx"
            :class="['eth-fc-cell', {
              'eth-fc-cell--blank': !cell,
              'eth-fc-cell--today': cell && cell.isToday
            }]"
            @click="onCellClick(cell)"
          >
            <div v-if="cell" class="eth-fc-cell-frame">
              <div class="eth-fc-cell-top">
                <span :class="['eth-fc-day-num', { 'eth-fc-day-num--today': cell.isToday }]">
                  {{ cell.day }}
                </span>
              </div>
              <div class="eth-fc-cell-events">
                <div
                  v-for="evt in cell.events"
                  :key="evt.id"
                  class="eth-fc-event"
                  :style="eventStyle(evt)"
                  @click.stop="onEventClick(evt)"
                  :title="evt.title"
                >
                  {{ evt.title }}
                </div>
                <div
                  v-if="cell.overflowCount > 0"
                  class="eth-fc-event-more"
                >
                  +{{ cell.overflowCount }} more
                </div>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { toEthiopian, toGregorian } from 'ethiopian-calendar-new';
import { MonthGrid } from 'kenat';
import { MONTHS_ETHIOPIAN } from '@/utils/const';

const MAX_VISIBLE_EVENTS = 3;

export default {
  name: 'EthiopianFullCalendar',
  props: {
    events: {
      type: Array,
      default: () => []
    }
  },
  emits: ['date-select', 'event-click', 'month-change'],
  data() {
    const now = new Date();
    const todayEth = toEthiopian(now.getFullYear(), now.getMonth() + 1, now.getDate());
    return {
      viewYear: todayEth.year,
      viewMonth: todayEth.month,
      todayEth
    };
  },
  computed: {
    monthTitle() {
      const name = MONTHS_ETHIOPIAN[this.viewMonth - 1] || `Month ${this.viewMonth}`;
      return `${name} ${this.viewYear}`;
    },
    mg() {
      return new MonthGrid({
        year: this.viewYear,
        month: this.viewMonth,
        weekStart: 0,
        useGeez: false,
        weekdayLang: 'english',
        mode: 'month',
        showAllSaints: false
      });
    },
    generated() {
      return this.mg.generate();
    },
    weekHeaders() {
      const headers = this.generated?.headers;
      if (headers && headers.length === 7) {
        return headers.map(h => {
          const s = String(h).trim();
          if (s.length > 3) return s.slice(0, 3);
          return s;
        });
      }
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    },
    flatCells() {
      const d = this.generated?.days || [];
      return Array.isArray(d[0]) ? d.flat() : d;
    },
    eventsByEthDate() {
      const map = {};
      if (!this.events) return map;

      this.events.forEach(evt => {
        if (!evt.start) return;
        try {
          const startDate = new Date(evt.start);
          const endDate = evt.end ? new Date(evt.end) : startDate;
          const current = new Date(startDate);
          let safety = 0;

          while (current <= endDate && safety < 60) {
            const eth = toEthiopian(current.getFullYear(), current.getMonth() + 1, current.getDate());
            const key = `${eth.year}-${eth.month}-${eth.day}`;
            if (!map[key]) map[key] = [];
            map[key].push(evt);
            current.setDate(current.getDate() + 1);
            safety++;
          }
        } catch (_) { /* skip malformed events */ }
      });

      return map;
    },
    weeks() {
      const cells = this.flatCells;

      const processed = cells.map(cell => {
        const dayNum = this.extractDayNumber(cell);
        if (!dayNum) return null;

        const cellMonth = this.extractMonth(cell);
        if (cellMonth !== null && cellMonth !== this.viewMonth) return null;

        const isToday =
          this.viewYear === this.todayEth.year &&
          this.viewMonth === this.todayEth.month &&
          dayNum === this.todayEth.day;

        const key = `${this.viewYear}-${this.viewMonth}-${dayNum}`;
        const allEvents = this.eventsByEthDate[key] || [];

        return {
          day: dayNum,
          isToday,
          events: allEvents.slice(0, MAX_VISIBLE_EVENTS),
          overflowCount: Math.max(0, allEvents.length - MAX_VISIBLE_EVENTS)
        };
      });

      const weeks = [];
      for (let i = 0; i < processed.length; i += 7) {
        const week = processed.slice(i, i + 7);
        while (week.length < 7) week.push(null);
        weeks.push(week);
      }

      return weeks;
    }
  },
  methods: {
    extractDayNumber(cell) {
      if (cell == null) return null;
      if (typeof cell === 'number') return cell;
      if (typeof cell === 'string' && /^\d+$/.test(cell)) return parseInt(cell, 10);
      return (
        cell.day ?? cell.d ?? cell.dayNumber ??
        (cell.ethiopian && cell.ethiopian.day) ??
        (cell.et && cell.et.day) ??
        (cell.ec && cell.ec.day) ??
        (cell.date && cell.date.day) ??
        (cell.value && cell.value.day) ??
        null
      );
    },
    extractMonth(cell) {
      if (cell == null || typeof cell !== 'object') return null;
      const et =
        cell.ethiopian || cell.et || cell.ec || cell.ethiopic || cell.ethiopianDate ||
        (cell.date && (cell.date.ethiopian || cell.date.et || cell.date.ec));
      return (et && et.month) || cell.month || null;
    },
    prevMonth() {
      if (this.viewMonth === 1) {
        this.viewMonth = 13;
        this.viewYear--;
      } else {
        this.viewMonth--;
      }
      this.emitMonthChange();
    },
    nextMonth() {
      if (this.viewMonth === 13) {
        this.viewMonth = 1;
        this.viewYear++;
      } else {
        this.viewMonth++;
      }
      this.emitMonthChange();
    },
    goToday() {
      const now = new Date();
      this.todayEth = toEthiopian(now.getFullYear(), now.getMonth() + 1, now.getDate());
      this.viewYear = this.todayEth.year;
      this.viewMonth = this.todayEth.month;
      this.emitMonthChange();
    },
    emitMonthChange() {
      const greg = toGregorian(this.viewYear, this.viewMonth, 1);
      const date = new Date(greg.year, greg.month - 1, greg.day);
      this.$emit('month-change', date);
    },
    onCellClick(cell) {
      if (!cell || !cell.day) return;
      const greg = toGregorian(this.viewYear, this.viewMonth, cell.day);
      const pad = n => String(n).padStart(2, '0');
      const startStr = `${greg.year}-${pad(greg.month)}-${pad(greg.day)}`;
      this.$emit('date-select', { startStr });
    },
    /** `backgroundColor`/`textColor` give a soft tinted event; `color` a solid one. */
    eventStyle(evt) {
      if (evt.backgroundColor || evt.textColor) {
        return { backgroundColor: evt.backgroundColor, borderColor: evt.backgroundColor, color: evt.textColor };
      }
      if (evt.color) return { backgroundColor: evt.color, borderColor: evt.color, color: '#ffffff' };
      return null;
    },
    onEventClick(evt) {
      this.$emit('event-click', evt);
    }
  },
  mounted() {
    this.emitMonthChange();
  }
};
</script>

<style scoped>
.eth-fc {
  font-family: inherit;
}

/* ── Toolbar ── */
.eth-fc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 14px;
}

.eth-fc-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.eth-fc-header-right {
  min-width: 80px;
}

.eth-fc-btn-group {
  display: inline-flex;
}

.eth-fc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #DCDCE0;
  background: #fff;
  color: #374151;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.eth-fc-btn:hover {
  background: #f3f4f6;
}

.eth-fc-btn:active {
  background: #e5e7eb;
}

.eth-fc-btn .pi {
  font-size: 0.7rem;
}

.eth-fc-btn--group-first {
  border-radius: 8px 0 0 8px;
}

.eth-fc-btn--group-last {
  border-radius: 0 8px 8px 0;
  border-left: none;
}

.eth-fc-btn--today {
  border-radius: 8px;
}

.eth-fc-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #18181b;
  margin: 0;
  user-select: none;
}

/* ── Table ── */
.eth-fc-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  border: 1px solid #e4e4e7;
}

.eth-fc-col-header {
  text-align: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #205410;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 8px 4px;
  background: #F1F1F0;
  border-bottom: 1px solid #E4E4E7;
}

/* ── Day cells ── */
.eth-fc-cell {
  border: 1px solid #e4e4e7;
  height: 90px;
  vertical-align: top;
  cursor: pointer;
  transition: background-color 0.12s;
  background: #fff;
}

.eth-fc-cell:hover:not(.eth-fc-cell--blank) {
  background-color: #f9fafb;
}

.eth-fc-cell--blank {
  background-color: #fafafa;
  cursor: default;
}

.eth-fc-cell--today {
  background-color: #f0f9f0;
}

.eth-fc-cell--today:hover {
  background-color: #e8f4e4;
}

.eth-fc-cell-frame {
  padding: 4px 5px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.eth-fc-cell-top {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 3px;
  flex-shrink: 0;
}

.eth-fc-day-num {
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.12s;
}

.eth-fc-day-num--today {
  background-color: #2E7918;
  color: #fff;
  font-weight: 700;
}

/* ── Events inside cells ── */
.eth-fc-cell-events {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.eth-fc-event {
  background-color: #e8f4e4;
  border: none;
  border-radius: 6px;
  padding: 2px 6px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #205410;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: background-color 0.12s, box-shadow 0.12s;
  line-height: 1.4;
}

.eth-fc-event:hover {
  filter: brightness(0.96);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.eth-fc-event-more {
  font-size: 0.65rem;
  font-weight: 600;
  color: #6b7280;
  padding: 1px 4px;
  cursor: default;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .eth-fc-header {
    gap: 8px;
  }

  .eth-fc-header-right {
    display: none;
  }

  .eth-fc-title {
    font-size: 1.05rem;
  }

  .eth-fc-cell {
    height: 70px;
  }

  .eth-fc-event {
    font-size: 0.6rem;
    padding: 1px 3px;
  }

  .eth-fc-day-num {
    width: 20px;
    height: 20px;
    font-size: 0.68rem;
  }

  .eth-fc-col-header {
    font-size: 0.6rem;
    padding: 6px 2px;
    letter-spacing: 0;
  }

  .eth-fc-btn {
    height: 28px;
    padding: 0 8px;
    font-size: 0.72rem;
  }

  .eth-fc-cell-top {
    margin-bottom: 1px;
  }
}

@media (max-width: 480px) {
  .eth-fc-title {
    font-size: 0.9rem;
  }

  .eth-fc-btn {
    height: 26px;
    padding: 0 6px;
    font-size: 0.68rem;
  }

  .eth-fc-header-left {
    gap: 6px;
  }

  .eth-fc-cell {
    height: 52px;
  }

  .eth-fc-cell-frame {
    padding: 2px 3px;
  }

  .eth-fc-day-num {
    width: 18px;
    height: 18px;
    font-size: 0.62rem;
  }

  .eth-fc-col-header {
    font-size: 0.5rem;
    padding: 4px 1px;
  }

  .eth-fc-event {
    font-size: 0.5rem;
    padding: 0 2px;
    border-left-width: 2px;
    line-height: 1.3;
  }

  .eth-fc-event-more {
    font-size: 0.5rem;
  }
}
</style>
